import { useState } from "react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { CATERING_AUDIENCES } from "@/lib/data";
import { submitLead } from "@/lib/api";

const initial = {
  name: "",
  company: "",
  email: "",
  phone: "",
  event_date: "",
  guest_count: "",
  event_type: "",
  message: "",
};

export default function Catering() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitLead({ type: "catering", ...form });
      toast.success("Inquiry received. We'll be in touch within 24 hours.");
      setForm(initial);
    } catch {
      toast.error("Couldn't submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="catering-page" className="bg-[#121212]">
      <section className="relative min-h-[60vh] flex items-end overflow-hidden">
        <img src="/brand-images/image1.jpg" alt="Catering" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-[#0a0a0a]/30" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 pb-16 md:pb-24 pt-32 w-full">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C99C3A] mb-4">Catering</p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.98] text-[#F5ECD7]">
            Bring{" "}
            <span className="text-[#C99C3A]">#TacoSoul</span>
            <br />
            to your next{" "}
            <span className="font-serif-italic font-normal">event</span>.
          </h1>
        </div>
      </section>

      {/* Audiences */}
      <section className="border-y border-[#F5ECD7]/10 py-10">
        <div className="max-w-7xl mx-auto px-5 md:px-8 flex flex-wrap gap-x-8 gap-y-4 text-[#F5ECD7]/60 text-sm uppercase tracking-[0.2em]">
          {CATERING_AUDIENCES.map((a) => (
            <span key={a} className="hover:text-[#C99C3A] transition-colors">{a}</span>
          ))}
        </div>
      </section>

      {/* Form */}
      <section className="section-pad">
        <div className="max-w-5xl mx-auto px-5 md:px-8 grid md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-4">
            <h2 className="font-display text-3xl md:text-4xl tracking-tighter leading-tight text-[#F5ECD7]">
              Tell us about your{" "}
              <span className="font-serif-italic font-normal text-[#C99C3A]">vision</span>.
            </h2>
            <p className="mt-5 text-[#F5ECD7]/65 leading-relaxed">
              We'll respond within 24 hours with a custom proposal. Most
              catering events book 4–8 weeks in advance.
            </p>
          </Reveal>
          <Reveal delay={120} className="md:col-span-8">
            <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
              <input data-testid="catering-name" required placeholder="Full Name *" value={form.name} onChange={set("name")} className="input-line md:col-span-1" />
              <input data-testid="catering-company" placeholder="Company / Organization" value={form.company} onChange={set("company")} className="input-line md:col-span-1" />
              <input data-testid="catering-email" required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className="input-line md:col-span-1" />
              <input data-testid="catering-phone" placeholder="Phone" value={form.phone} onChange={set("phone")} className="input-line md:col-span-1" />
              <input data-testid="catering-date" type="date" placeholder="Event Date" value={form.event_date} onChange={set("event_date")} className="input-line md:col-span-1" />
              <input data-testid="catering-count" placeholder="Guest Count" value={form.guest_count} onChange={set("guest_count")} className="input-line md:col-span-1" />
              <select data-testid="catering-event-type" value={form.event_type} onChange={set("event_type")} className="input-line md:col-span-2 bg-[#121212]">
                <option value="">Event Type</option>
                {CATERING_AUDIENCES.map((a) => (<option key={a} value={a}>{a}</option>))}
                <option value="Other">Other</option>
              </select>
              <textarea data-testid="catering-message" rows={4} placeholder="Tell us a bit more about your event..." value={form.message} onChange={set("message")} className="input-line md:col-span-2 resize-none" />
              <div className="md:col-span-2 mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <button data-testid="catering-submit" type="submit" disabled={loading} className="btn-gold">
                  {loading ? "Sending..." : "Send Inquiry"}
                </button>
                <span className="text-xs text-[#F5ECD7]/50">We never share your information.</span>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
